import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  let dropdownTimer = null;

  const navLinks = [
    { name: 'Home', path: '/' },
    {
      name: 'About',
      path: '/about',
      dropdown: [
        { name: 'About Us', path: '/about' },
        { name: 'News', path: '/news' }
      ]
    },
    { name: 'Ministries', path: '/ministries' },
    { name: 'Encounters', path: '/encounters' },
    { name: 'Blog', path: '/blog' },
    { name: 'Light a Candle', path: '/light-a-candle' },
    { name: 'Voices', path: '/voices' },
    { name: 'Free Notary', path: '/notary' },
    {
      name: 'Resource Directory',
      path: '/resources',
      dropdown: [
        { name: 'Housing / Rental', path: '/resources/housing' },
        { name: 'Food', path: '/resources/food' },
        { name: 'Clothing', path: '/resources/clothing' },
        { name: 'Social Services', path: '/resources/social-services' },
        { name: 'Mental / Behavioral Health', path: '/resources/mental-health' },
        { name: 'Abuse / Domestic Violence', path: '/resources/domestic-violence' }
      ]
    },
    { name: 'Get Involved', path: '/get-involved' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;
  const isDropdownActive = (link) =>
    link.dropdown && link.dropdown.some((d) => location.pathname === d.path || location.pathname.startsWith(d.path));

  const openDropdown = (name) => {
    if (dropdownTimer) clearTimeout(dropdownTimer);
    setActiveDropdown(name);
  };
  const closeDropdown = () => {
    dropdownTimer = setTimeout(() => setActiveDropdown(null), 180);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-sm border-b border-amber-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group flex-shrink-0">
            <img
              src="/images/header-logo.png"
              alt="tryHimandsee ministries"
              className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-110"
            />
            <div className="block">
              <h1 className="text-lg xl:text-xl font-bold text-amber-400 leading-tight">tryHimandsee</h1>
              <p className="text-xs text-amber-200/80 leading-tight">ministries</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-0.5">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => openDropdown(link.name)}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    data-testid={`nav-dropdown-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    className={`px-2 py-2 rounded-lg text-[12px] xl:text-[13px] font-medium transition-all duration-300 flex items-center whitespace-nowrap cursor-pointer ${
                      isActive(link.path) || isDropdownActive(link)
                        ? 'bg-amber-500 text-slate-900'
                        : 'text-amber-100 hover:bg-amber-500/10 hover:text-amber-300'
                    }`}
                  >
                    {link.name}
                    <ChevronDown size={12} className="ml-0.5" />
                  </button>
                  {activeDropdown === link.name && (
                    <div
                      data-testid={`nav-dropdown-menu-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      className="absolute top-full left-0 mt-2 w-60 bg-slate-900 border border-amber-500/20 rounded-lg shadow-xl overflow-hidden z-50"
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setActiveDropdown(null)}
                          className={`block px-4 py-3 text-sm transition-colors ${
                            isActive(item.path)
                              ? 'bg-amber-500 text-slate-900 font-semibold'
                              : 'text-amber-100 hover:bg-amber-500/10 hover:text-amber-300'
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`px-2 py-2 rounded-lg text-[12px] xl:text-[13px] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    isActive(link.path)
                      ? 'bg-amber-500 text-slate-900'
                      : 'text-amber-100 hover:bg-amber-500/10 hover:text-amber-300'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <Link
              to="/donate"
              className="ml-1.5 px-3 xl:px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-lg font-semibold hover:from-amber-400 hover:to-amber-500 transition-all duration-300 shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 whitespace-nowrap text-xs xl:text-sm"
            >
              Donate
            </Link>
            <ThemeToggle />
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors ml-2"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden pb-4 animate-in slide-in-from-top duration-300">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div key={link.name} className="space-y-1">
                    <div className="text-amber-400 font-semibold text-sm px-4 py-2 uppercase tracking-wider">
                      {link.name}
                    </div>
                    {link.dropdown.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={`block px-8 py-2 rounded-lg text-sm transition-colors ${
                          isActive(item.path)
                            ? 'bg-amber-500 text-slate-900 font-semibold'
                            : 'text-amber-100 hover:bg-amber-500/10'
                        }`}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive(link.path)
                        ? 'bg-amber-500 text-slate-900'
                        : 'text-amber-100 hover:bg-amber-500/10'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
              <Link
                to="/donate"
                onClick={() => setIsMenuOpen(false)}
                className="px-4 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-lg font-semibold text-center hover:from-amber-400 hover:to-amber-500 transition-all"
              >
                Donate
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
